import React, { useMemo } from 'react';
import { Box, Button, Chip, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { FrontendNotebook } from '../../contexts/NotebooksContext';
import type { SocialMediaPost } from '../../types/types';

type EnrichedPost = SocialMediaPost & { clusterTitle: string };

function normalizePlatform(raw: string): string {
  const s = (raw || '').trim().toLowerCase();
  if (s.includes('facebook')) return 'Facebook';
  if (s === 'x' || s.includes('twitter')) return 'X';
  if (s.includes('linkedin')) return 'LinkedIn';
  if (s.includes('instagram')) return 'Instagram';
  const t = raw.trim();
  return t || 'Other';
}

function platformSortKey(p: string): number {
  const order = ['Facebook', 'X', 'LinkedIn', 'Instagram', 'Other'];
  const i = order.indexOf(p);
  return i === -1 ? 99 : i;
}

function collectClusterSocialPosts(nb: FrontendNotebook | undefined): EnrichedPost[] {
  if (!nb?.clusters?.length) return [];
  const out: EnrichedPost[] = [];
  for (const cl of nb.clusters) {
    const list = cl.social_media || [];
    for (const sm of list) {
      out.push({ ...sm, clusterTitle: cl.title || 'Cluster' });
    }
  }
  return out.sort((a, b) => (a.post_number ?? 0) - (b.post_number ?? 0));
}

const railShellSx = {
  rail: {
    flexShrink: 0,
    borderLeft: { md: '1px solid #27272a' },
    borderTop: { xs: '1px solid #27272a', md: 'none' },
    pl: { md: 2.5 },
    pt: { xs: 2, md: 0 },
  } as const,
  drawer: {
    width: '100%',
    maxWidth: '100%',
    flexShrink: 0,
    px: 2,
    py: 1.5,
    border: 'none',
    maxHeight: 'calc(100vh - 88px)',
    overflowY: 'auto',
  } as const,
};

export function CampaignClusterSocialRail({
  notebook,
  inDrawer = false,
  onAddToCalendar,
}: {
  notebook: FrontendNotebook | undefined;
  /** When true, fill a wide drawer (no side-rail chrome / sticky). */
  inDrawer?: boolean;
  onAddToCalendar?: (post: { platform: string; body: string; hashtags: string }) => void;
}) {
  const posts = useMemo(() => collectClusterSocialPosts(notebook), [notebook]);
  const shell = inDrawer ? railShellSx.drawer : { ...railShellSx.rail, width: { xs: '100%', md: 380 } };

  const byPlatform = useMemo(() => {
    const m = new Map<string, EnrichedPost[]>();
    for (const p of posts) {
      const plat = normalizePlatform(p.platform);
      if (!m.has(plat)) m.set(plat, []);
      m.get(plat)!.push(p);
    }
    return m;
  }, [posts]);

  const platforms = useMemo(() => {
    const keys: string[] = [];
    byPlatform.forEach((_posts, plat) => {
      keys.push(plat);
    });
    return keys.sort((a, b) => platformSortKey(a) - platformSortKey(b));
  }, [byPlatform]);

  const copyBody = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  if (!notebook) {
    return (
      <Box sx={{ ...shell }}>
        <Typography variant="subtitle2" sx={{ color: '#71717a' }}>
          Reference article not loaded — open Social Studio again after your library syncs.
        </Typography>
      </Box>
    );
  }

  if (posts.length === 0) {
    return (
      <Box sx={{ ...shell }}>
        <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.5 }}>
          No cluster social posts yet. Generate content for clusters on this article in the Library (Insights → Related
          clusters → Generate draft), then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...(inDrawer
          ? shell
          : {
              width: { xs: '100%', md: 400 },
              maxWidth: '100%',
              ...railShellSx.rail,
              pl: { md: 2.5 },
              pt: { xs: 2, md: 0 },
              position: { md: 'sticky' },
              top: { md: 16 },
              alignSelf: 'flex-start',
              maxHeight: { md: 'calc(100vh - 120px)' },
              overflowY: 'auto',
              pb: 2,
            }),
      }}
    >
      <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5 }}>From this article</Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
        Cluster copy stays here. Add a post to the calendar when it should be scheduled or sent.
      </Typography>
      <Grid container spacing={2}>
        {platforms.map((plat) => {
          const platPosts = byPlatform.get(plat) || [];
          const byAngle = new Map<string, EnrichedPost[]>();
          for (const post of platPosts) {
            const angle = (post.post_type || 'Post').trim() || 'Post';
            if (!byAngle.has(angle)) byAngle.set(angle, []);
            byAngle.get(angle)!.push(post);
          }
          const angles: string[] = [];
          byAngle.forEach((_posts, angle) => {
            angles.push(angle);
          });
          angles.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

          return (
            <Grid item xs={12} md={6} key={plat}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e4e4e7', mb: 1.5 }}>
                {plat}
              </Typography>
              <Stack spacing={2}>
                {angles.map((angle) => (
                  <Box key={`${plat}-${angle}`}>
                    <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, letterSpacing: 0.02 }}>
                      {angle}
                    </Typography>
                    <Stack spacing={1.25} sx={{ mt: 0.75 }}>
                      {(byAngle.get(angle) || []).map((post, idx) => (
                        <Box
                          key={`${plat}-${angle}-${post.post_number}-${idx}`}
                          sx={{
                            bgcolor: '#18181b',
                            border: '1px solid #3f3f46',
                            borderRadius: 1,
                            p: 1.25,
                            position: 'relative',
                          }}
                        >
                          <Tooltip title="Copy post text">
                            <IconButton
                              size="small"
                              onClick={() => void copyBody(post.post_content)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                color: '#a1a1aa',
                                '&:hover': { color: '#60a5fa' },
                              }}
                              aria-label="Copy post"
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="caption" sx={{ color: '#52525b', display: 'block', pr: 4, mb: 0.5 }}>
                            {post.clusterTitle}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#f4f4f5',
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.45,
                              pr: 1,
                            }}
                          >
                            {post.post_content}
                          </Typography>
                          {onAddToCalendar && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                onAddToCalendar({
                                  platform: plat,
                                  body: post.post_content,
                                  hashtags: (post.hashtags || []).join(' '),
                                })
                              }
                              sx={{ mt: 1.25, color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none' }}
                            >
                              Add to calendar
                            </Button>
                          )}
                          {(post.hashtags?.length ?? 0) > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              {(post.hashtags || []).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag.startsWith('#') ? tag : `#${tag}`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    bgcolor: '#0c1829',
                                    color: '#93c5fd',
                                    border: '1px solid #1d4ed8',
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
